import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spinner } from "../../../components";
import Dashboard from "../../../layout/Dashboard";
import { CreditForm } from "../Forms";
import { ResponsiveAlert } from "../Alerts";
import { Api } from "../../../api/Assinafy";

const PaymentUpdate = () => {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  const [openAlert, setOpenAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [alertMessage, setAlertMessage] = useState("Cartão atualizado com sucesso");
  const [alertType, setAlertType] = useState("success");
  const [forceValidation, setForceValidation] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [needsCustomerUpdate, setNeedsCustomerUpdate] = useState(false);

  const fetchCustomerData = async () => {
    try {
      const response = await Api("customer/view")();

      if (!isMountedRef.current) return;

      if (response?.status === 200 && response?.data?.data) {
        setCustomerData(response.data.data);

        const customer = response.data.data;
        const requiredFields = ["name", "email", "taxpayer_id", "postal_code", "address_line1", "city", "state"];
        const missingFields = requiredFields.filter((field) => !customer[field]);

        if (missingFields.length > 0) {
          setNeedsCustomerUpdate(true);
        }
      } else {
        setNeedsCustomerUpdate(true);
      }
    } catch (error) {
      console.error("Erro ao buscar dados do cliente:", error);
      if (isMountedRef.current) {
        setNeedsCustomerUpdate(true);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingData(false);
      }
    }
  };

  const handleUpdateCard = async () => {
    if (loading) return;

    if (!isFormValid) {
      setForceValidation(true);
      return;
    }

    try {
      setLoading(true);
      setForceValidation(false);

      const creditCardPayload = {
          "credit_card": {
          number: formData.number.replace(/\s/g, ""),
          name_on_card: formData.name_on_card,
          expiration_month: parseInt(formData.expiration_month),
          expiration_year: parseInt(formData.expiration_year),
          security_code: parseInt(formData.security_code),
          holder: {
            name: formData.holder_name,
            email: formData.holder_email,
            taxpayer_id: formData.holder_taxpayer_id.replace(/\D/g, ""),
            postal_code: formData.postal_code.replace(/\D/g, ""),
            address_line1: formData.address_line1,
            address_line2: formData.address_line2 || "",
            house_number: formData.house_number,
            phone_number: formData.holder_phone.replace(/\D/g, ""),
            neighborhood: formData.neighborhood,
            city: formData.city,
            state: formData.state,
          },
        }
      };

      const response = await Api("subscription/updatePaymentMethod")(creditCardPayload);

      if (!isMountedRef.current) return;

      if (response?.status === 200) {
        setAlertType("success");
        setAlertMessage("Cartão atualizado com sucesso");
        setOpenAlert(true);

        setTimeout(() => {
          if (isMountedRef.current) {
            navigate("/dashboard/workspace/plan");
          }
        }, 2000);
      } else {
        throw new Error(response?.data?.message || "Erro ao atualizar cartão");
      }
    } catch (error) {
      console.error("Erro ao atualizar cartão:", error);
      if (isMountedRef.current) {
        setAlertType("error");
        setAlertMessage(error?.response?.data?.message || error.message || "Erro ao atualizar cartão. Tente novamente.");
        setOpenAlert(true);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleFormChange = (data) => {
    setFormData(data.formData);
    setIsFormValid(data.isValid);
  };

  useEffect(() => {
    fetchCustomerData();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (loadingData) {
    return (
      <Dashboard.Content style={{ maxWidth: "688px" }}>
        <Dashboard.Header
          returnButton={true}
          title="Atualize seu cartão"
          className="d-flex reverse-row"
        />
        <div className="text-center p-4">
          <Spinner />
          <p className="mt-3">Carregando...</p>
        </div>
      </Dashboard.Content>
    );
  }

  return (
    <Dashboard.Content style={{ maxWidth: "688px" }}>
      <Dashboard.Header
        returnButton={true}
        title="Atualize seu cartão"
        className="d-flex reverse-row"
      />
      <CreditForm 
        onFormChange={handleFormChange} 
        forceValidation={forceValidation}
      />
      <div className="d-flex flex-column flex-lg-row w-100 mt-3 pt-3 gap-3">
        <Button
          variant="primary"
          onClick={handleUpdateCard}
          className="text-uppercase"
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner className="spinner-small" />
              Salvando...
            </>
          ) : (
            <>Salvar alterações</>
          )}
        </Button>
        <Button 
          variant="border" 
          className="text-uppercase"
          onClick={() => navigate("/dashboard/workspace/plan")}
          disabled={loading}
        >
          Cancelar
        </Button>
      </div>
      <ResponsiveAlert
        show={openAlert}
        onClose={() => setOpenAlert(false)}
        text={alertMessage}
        variant={alertType}
      />
    </Dashboard.Content>
  );
};

export default PaymentUpdate;
